import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import Marquee from "@/components/Marquee";
import BeforeAfter from "@/components/BeforeAfter";
import CtaFinal from "@/components/CtaFinal";
import { Reveal, MaskedLine, MaskedLineInView, Eyebrow } from "@/components/Reveal";
import { IMG, SERVICES, PROCESS_STEPS, REASONS, COMUNI, PHONE_TEL } from "@/data/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.1]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden bg-carbone">
      <motion.div style={{ y }} className="absolute inset-0 -top-[10%] h-[120%]">
        <img
          src={IMG.hero}
          alt="Interno di casa ristrutturata: soggiorno moderno e luminoso"
          className="w-full h-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-carbone via-carbone/45 to-carbone/25" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto w-full px-5 md:px-8 pt-36 pb-10 md:pb-14">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-display text-[11px] md:text-xs tracking-[0.35em] uppercase text-ivory/70 mb-6 md:mb-8 flex items-center gap-4"
        >
          <span className="h-px w-10 bg-terracotta" />
          Impresa edile · Ristrutturazioni · Chiavi in mano
        </motion.p>

        <h1 className="font-display font-medium text-ivory text-[13vw] sm:text-6xl lg:text-8xl tracking-tight leading-[0.98]">
          <MaskedLine delay={0.35}>Ristrutturiamo casa.</MaskedLine>
          <MaskedLine delay={0.5}>
            <span className="font-serif italic font-normal text-ivory/95">Pensiamo noi a tutto.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 md:mt-8 max-w-xl text-ivory/75 text-base md:text-lg leading-relaxed"
        >
          Ristrutturazioni complete e lavori edili, seguiti con ordine dalla
          prima valutazione fino alle finiture.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-4"
        >
          <Link
            to="/contatti"
            data-testid="hero-cta-sopralluogo"
            className="group inline-flex items-center justify-center gap-3 bg-terracotta hover:bg-terracotta-hover text-ivory font-display text-sm tracking-[0.15em] uppercase px-8 py-4 md:px-10 md:py-5 transition-colors duration-300"
          >
            Richiedi un sopralluogo
            <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href={PHONE_TEL}
            data-testid="hero-cta-call"
            className="inline-flex items-center justify-center gap-3 border border-ivory/40 hover:border-ivory hover:bg-ivory/5 text-ivory font-display text-sm tracking-[0.15em] uppercase px-8 py-4 md:px-10 md:py-5 transition-[border-color,background-color] duration-300"
          >
            <Phone size={16} strokeWidth={1.5} />
            Chiama ora
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.25, duration: 1 }}
          data-testid="hero-trust-indicators"
          className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-3 border-t border-ivory/20"
        >
          {["Esperienza sul campo", "Ristrutturazioni chiavi in mano", "Un unico referente per il cantiere"].map((t, i) => (
            <div key={t} className={`py-5 sm:py-6 ${i > 0 ? "sm:border-l border-ivory/20 sm:pl-8" : ""} ${i < 2 ? "sm:pr-8" : ""}`}>
              <p className="font-display text-[11px] md:text-xs tracking-[0.25em] uppercase text-ivory/70">{t}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

const Home = () => (
  <>
    <SEO
      title="SOPAJ — Impresa Edile a Nibionno | Ristrutturazioni Chiavi in Mano a Lecco e Brianza"
      description="Impresa edile con sede a Nibionno, specializzata in ristrutturazioni complete e lavori chiavi in mano. Una sola impresa per tutto il cantiere: interventi a Nibionno, Lecco, Brianza e in altre località."
      path="/"
    />
    <Hero />
    <Marquee />

    <section data-testid="home-impresa" className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-36">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-6">
          <Reveal><Eyebrow number="01" label="L'impresa" /></Reveal>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-anthracite">
            <MaskedLineInView>Conosci chi entra</MaskedLineInView>
            <MaskedLineInView delay={0.1}>
              <span className="font-serif italic font-normal">in casa tua.</span>
            </MaskedLineInView>
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 text-anthracite/75 leading-relaxed">
              Prima di aprire un cantiere è giusto sapere chi ci lavorerà. Sopaj è
              un'impresa edile con sede a Nibionno: ci presentiamo di persona,
              valutiamo l'intervento e seguiamo i lavori mettendoci la faccia.
            </p>
            <p className="mt-4 text-anthracite/75 leading-relaxed">
              Appartamenti, ville e abitazioni: dalla singola lavorazione alla
              ristrutturazione completa, il cantiere resta nelle nostre mani e tu
              sai sempre a chi parlare.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <Link
              to="/impresa"
              data-testid="home-impresa-cta"
              className="group mt-10 inline-flex items-center gap-3 font-display text-sm tracking-[0.15em] uppercase text-terracotta"
            >
              <span className="link-underline">Conosci l'impresa</span>
              <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-6 relative">
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -top-5 -left-5 w-full h-full border border-terracotta/40" aria-hidden="true" />
              <img
                src={IMG.impresa}
                alt="Interno ristrutturato da impresa edile: soggiorno contemporaneo"
                className="relative w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -right-3 md:-right-6 bg-terracotta text-ivory px-7 py-5">
                <p className="font-display text-2xl tracking-tight leading-snug">Cantiere<br />seguito</p>
                <p className="font-display text-[10px] tracking-[0.25em] uppercase text-ivory/80 mt-2">Passo dopo passo</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section data-testid="home-chiavi-in-mano" className="bg-sand/60">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow number="02" label="Chiavi in mano" /></Reveal>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-anthracite">
              <MaskedLineInView>La tua ristrutturazione,</MaskedLineInView>
              <MaskedLineInView delay={0.1}>
                <span className="font-serif italic font-normal">dall'inizio alla fine.</span>
              </MaskedLineInView>
            </h2>
            <Reveal delay={0.2}>
              <p className="mt-8 text-anthracite/75 leading-relaxed">
                Niente telefonate agli artigiani, niente preventivi da confrontare,
                niente imprevisti da gestire da solo: con la formula chiavi in mano
                organizziamo tempi, lavorazioni e maestranze.
              </p>
              <p className="mt-4 text-anthracite/75 leading-relaxed">
                Tu segui l'avanzamento e prendi le decisioni importanti.
                Di demolizioni, impianti, posa e finiture ci occupiamo noi.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link
                to="/chiavi-in-mano"
                data-testid="home-process-cta"
                className="group mt-10 inline-flex items-center gap-3 font-display text-sm tracking-[0.15em] uppercase text-terracotta"
              >
                <span className="link-underline">Come funziona</span>
                <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ol className="grid grid-cols-1 sm:grid-cols-2 border-t border-l border-anthracite/10">
              {PROCESS_STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.07}>
                  <li data-testid={`process-step-${s.n}`} className="border-b border-r border-anthracite/10 p-6 md:p-8 bg-panna/40">
                    <p className="font-display text-terracotta text-sm tracking-[0.2em]">{s.n}</p>
                    <p className="mt-3 font-display text-lg md:text-xl tracking-tight text-anthracite">{s.title}</p>
                  </li>
                </Reveal>
              ))}
              <Reveal delay={0.5}>
                <li className="border-b border-r border-anthracite/10 p-6 md:p-8 bg-terracotta text-ivory flex items-center">
                  <Link to="/contatti" data-testid="process-cta-sopralluogo" className="group inline-flex items-center gap-3 font-display text-sm tracking-[0.15em] uppercase">
                    Parliamo del tuo progetto
                    <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </li>
              </Reveal>
            </ol>
          </div>
        </div>
      </div>
    </section>

    <section data-testid="home-servizi" className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-36">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14 md:mb-20">
        <div>
          <Reveal><Eyebrow number="03" label="Servizi" /></Reveal>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight text-anthracite">
            <MaskedLineInView>
              Le lavorazioni che <span className="font-serif italic font-normal">seguiamo ogni giorno.</span>
            </MaskedLineInView>
          </h2>
        </div>
        <Reveal delay={0.15}>
          <Link
            to="/servizi"
            data-testid="home-servizi-cta"
            className="group inline-flex items-center gap-3 font-display text-sm tracking-[0.15em] uppercase text-terracotta shrink-0"
          >
            <span className="link-underline">Tutti i servizi</span>
            <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug} delay={(i % 3) * 0.08}>
            <Link to={`/${s.slug}`} data-testid={`home-service-${s.slug}`} className="group block">
              <div className="overflow-hidden aspect-[4/3] bg-sand">
                <img
                  src={s.img}
                  alt={`${s.title} — impresa edile Sopaj, Nibionno`}
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="mt-5 flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display text-xl tracking-tight text-anthracite group-hover:text-terracotta transition-colors duration-300">{s.title}</h3>
                  <p className="mt-2 text-sm text-concrete leading-relaxed">{s.text}</p>
                </div>
                <ArrowRight size={18} strokeWidth={1.5} className="mt-1 shrink-0 text-anthracite/30 transition-all duration-300 group-hover:text-terracotta group-hover:translate-x-1" />
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>

    <section data-testid="home-prima-dopo" className="bg-anthracite text-ivory grain relative">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4">
            <Reveal><Eyebrow number="04" label="Prima / Dopo" dark /></Reveal>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              <MaskedLineInView>Il cambiamento</MaskedLineInView>
              <MaskedLineInView delay={0.1}>
                <span className="font-serif italic font-normal">si vede.</span>
              </MaskedLineInView>
            </h2>
            <Reveal delay={0.2}>
              <p className="mt-8 text-ivory/60 leading-relaxed">
                Trascina il cursore e confronta le due fasi dell'intervento:
                lo stesso ambiente, dallo stato iniziale alla consegna.
              </p>
              <p className="mt-4 text-ivory/40 text-sm leading-relaxed">
                Un confronto reale documentato in cantiere: questa sezione
                verrà arricchita con i prossimi prima/dopo dei lavori Sopaj.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.15}>
              <BeforeAfter beforeImg={IMG.prima} afterImg={IMG.dopo} />
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    <section data-testid="home-perche-sopaj" className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-36">
      <Reveal><Eyebrow number="05" label="Perché Sopaj" /></Reveal>
      <h2 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-anthracite max-w-3xl">
        <MaskedLineInView>Cosa significa</MaskedLineInView>
        <MaskedLineInView delay={0.1}>
          <span className="font-serif italic font-normal">lavorare con noi.</span>
        </MaskedLineInView>
      </h2>
      <div className="mt-14 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-anthracite/10">
        {REASONS.map((r, i) => (
          <Reveal key={r.n} delay={(i % 3) * 0.08}>
            <div data-testid={`reason-${r.n}`} className="border-b border-r border-anthracite/10 p-8 md:p-10 h-full">
              <p className="font-display text-terracotta text-sm tracking-[0.2em]">{r.n}</p>
              <h3 className="mt-4 font-display text-xl md:text-2xl tracking-tight text-anthracite">{r.title}</h3>
              <p className="mt-3 text-sm text-concrete leading-relaxed">{r.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section data-testid="home-recensioni" className="bg-sand/60">
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-24 md:py-32 text-center">
        <Reveal><Eyebrow number="06" label="Recensioni" /></Reveal>
        <h2 className="mt-6 font-serif italic text-4xl sm:text-5xl tracking-tight text-anthracite">
          <MaskedLineInView>La fiducia si costruisce nel tempo.</MaskedLineInView>
        </h2>
        <Reveal delay={0.2}>
          <p className="mt-8 text-anthracite/70 leading-relaxed max-w-2xl mx-auto">
            Lavoriamo soprattutto grazie al passaparola di chi ci ha già
            scelto. Questa sezione è pronta ad accogliere le recensioni autentiche
            dei nostri clienti, che pubblicheremo appena disponibili.
          </p>
        </Reveal>
      </div>
    </section>

    <section data-testid="home-zona-operativa" className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-36">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal><Eyebrow number="07" label="Dove operiamo" /></Reveal>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight leading-[1.08] text-anthracite">
            <MaskedLineInView>
              Dove c'è un progetto, <span className="font-serif italic font-normal">possiamo esserci.</span>
            </MaskedLineInView>
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 text-anthracite/75 leading-relaxed">
              Sopaj segue interventi di ristrutturazione e lavori edili senza limitarsi
              a una singola area geografica. Valutiamo ogni progetto in base alla
              tipologia dell'intervento, alle esigenze del cliente e all'organizzazione
              del cantiere, offrendo la nostra disponibilità anche al di fuori della
              zona in cui ha sede l'impresa.
            </p>
            <p className="mt-4 text-anthracite/75 leading-relaxed">
              Realizziamo ristrutturazioni e lavori edili a Nibionno, Lecco, in Brianza
              e in altre località: la sede è locale, l'operatività non ha confini rigidi.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal>
            <p className="font-display text-xs tracking-[0.25em] uppercase text-concrete mb-6">
              Alcune delle aree in cui operiamo
            </p>
          </Reveal>
          <div className="flex flex-wrap gap-3 content-start">
            {COMUNI.map((c, i) => (
              <Reveal key={c} delay={i * 0.04}>
                <span data-testid={`comune-${c.toLowerCase()}`} className="inline-block border border-anthracite/20 px-5 py-2.5 font-display text-sm tracking-wide text-anthracite/80 hover:border-terracotta hover:text-terracotta transition-colors duration-300">
                  {c}
                </span>
              </Reveal>
            ))}
            <Reveal delay={0.5}>
              <span data-testid="comune-altre-localita" className="inline-block bg-terracotta text-ivory px-5 py-2.5 font-display text-sm tracking-wide">
                …e altre località
              </span>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    <CtaFinal />
  </>
);

export default Home;
